import { useEffect, useState } from 'react';
import {
  collection,
  getFirestore,
  onSnapshot,
  query,
  updateDoc,
  doc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/core/firebase';
import type { Client } from '../types';

interface ClientDraft {
  displayName?: string;  // Optional - trainer's name for client
  phone: string;         // Required - E.164 format
}

interface CreateInviteResult {
  inviteId: string;
  magicLoginToken: string;
}

export function useTrainerClients(trainerId?: string) {
  const [clients, setClients] = useState<Client[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const firestore = getFirestore();

  // Subscribe to trainer's clients
  useEffect(() => {
    if (!trainerId) return;
    const q = query(collection(db, 'clients'), where('trainerId', '==', trainerId));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as Client)
      );
      setClients(docs);
    });
    return () => unsub();
  }, [trainerId]);

  /**
   * Create a new client invite via Cloud Function.
   * Handles all validation and duplicate checking server-side.
   */
  const addClient = async (draft: ClientDraft): Promise<CreateInviteResult | undefined> => {
    if (!trainerId) {
      setError('Missing trainer id');
      return;
    }
    
    const normalizedPhone = draft.phone.trim();
    if (!/^\+\d{8,15}$/.test(normalizedPhone)) {
      setError('Phone must be in E.164 format, like +16025551234');
      return;
    }
    
    setError(null);
    setLoading(true);

    try {
      const createInviteFn = httpsCallable<
        { phone: string; displayName?: string },
        CreateInviteResult
      >(functions, 'createClientInvite');

      const result = await createInviteFn({
        phone: normalizedPhone,
        displayName: draft.displayName?.trim(),
      });

      return result.data;
    } catch (err: any) {
      // Extract error message from Cloud Function error
      const message = err?.message || 'Failed to create invite';
      setError(message);
      return;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate a new magic login token for an existing client.
   * (Still client-side as it's a simple field update)
   */
  const regenerateMagicLink = async (clientId: string) => {
    const token = crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, '')
      : Array.from({ length: 32 }, () => 
          'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'[
            Math.floor(Math.random() * 62)
          ]
        ).join('');
        
    await updateDoc(doc(firestore, 'clients', clientId), { magicLoginToken: token });
    return token;
  };

  return { clients, addClient, regenerateMagicLink, error, loading };
}
